// client/src/coms/views/canvas_parts/CanvasToolControlsPanel.js
import React from 'react';
import CanvasSurfaceMediaControls from './CanvasSurfaceMediaControls';
import { MAX_AIRBRUSH_DISTANCE, MIN_AIRBRUSH_DISTANCE } from './canvas_room_view_parts/airbrushUtils';

function clampNumber(value, min, max, fallback = min) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(max, Math.max(min, number));
}



const ERASER_SIZE_MIN = 1;
const ERASER_SIZE_DETAIL_LIMIT = 150;
const ERASER_SIZE_MAX = 1000;
const ERASER_SIZE_DETAIL_FRACTION = 0.8;
const ERASER_SIZE_SLIDER_STEPS = 10000;

function eraserSizeToSliderValue(value) {
  const size = clampNumber(value, ERASER_SIZE_MIN, ERASER_SIZE_MAX, ERASER_SIZE_MIN);
  const normalized = size <= ERASER_SIZE_DETAIL_LIMIT
    ? ((size - ERASER_SIZE_MIN) / (ERASER_SIZE_DETAIL_LIMIT - ERASER_SIZE_MIN)) * ERASER_SIZE_DETAIL_FRACTION
    : ERASER_SIZE_DETAIL_FRACTION
      + ((size - ERASER_SIZE_DETAIL_LIMIT) / (ERASER_SIZE_MAX - ERASER_SIZE_DETAIL_LIMIT))
        * (1 - ERASER_SIZE_DETAIL_FRACTION);
  return Math.round(normalized * ERASER_SIZE_SLIDER_STEPS);
}

function sliderValueToEraserSize(value) {
  const normalized = clampNumber(value, 0, ERASER_SIZE_SLIDER_STEPS, 0) / ERASER_SIZE_SLIDER_STEPS;
  if (normalized <= ERASER_SIZE_DETAIL_FRACTION) {
    return Math.round(
      ERASER_SIZE_MIN
      + (normalized / ERASER_SIZE_DETAIL_FRACTION)
        * (ERASER_SIZE_DETAIL_LIMIT - ERASER_SIZE_MIN),
    );
  }
  return Math.round(
    ERASER_SIZE_DETAIL_LIMIT
    + ((normalized - ERASER_SIZE_DETAIL_FRACTION) / (1 - ERASER_SIZE_DETAIL_FRACTION))
      * (ERASER_SIZE_MAX - ERASER_SIZE_DETAIL_LIMIT),
  );
}

function EraserSizeControl({ value, onChange, disabled = false }) {
  const safeValue = Math.round(clampNumber(value, ERASER_SIZE_MIN, ERASER_SIZE_MAX, ERASER_SIZE_MIN));

  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5, opacity: disabled ? 0.5 : 1 }}>
      <span style={{ display: 'flex', justifyContent: 'space-between', gap: 10, color: 'rgba(255,255,255,0.88)', fontSize: 12, fontWeight: 800 }}>
        <span>Size</span>
        <span>{safeValue}px</span>
      </span>
      <input
        type="range"
        min={0}
        max={ERASER_SIZE_SLIDER_STEPS}
        step={1}
        value={eraserSizeToSliderValue(safeValue)}
        disabled={disabled}
        onChange={(event) => {
          if (typeof onChange === 'function') onChange(sliderValueToEraserSize(event.target.value));
        }}
        style={{ width: '100%', margin: 0, accentColor: 'rgba(255,255,255,0.92)' }}
      />
    </label>
  );
}

const PEN_SIZE_MIN = 1;
const PEN_SIZE_DETAIL_LIMIT = 50;
const PEN_SIZE_MAX = 2000;
const PEN_SIZE_DETAIL_FRACTION = 0.75;
const PEN_SIZE_SLIDER_STEPS = 10000;

function penSizeToSliderValue(value) {
  const size = clampNumber(value, PEN_SIZE_MIN, PEN_SIZE_MAX, PEN_SIZE_MIN);
  const normalized = size <= PEN_SIZE_DETAIL_LIMIT
    ? ((size - PEN_SIZE_MIN) / (PEN_SIZE_DETAIL_LIMIT - PEN_SIZE_MIN)) * PEN_SIZE_DETAIL_FRACTION
    : PEN_SIZE_DETAIL_FRACTION
      + ((size - PEN_SIZE_DETAIL_LIMIT) / (PEN_SIZE_MAX - PEN_SIZE_DETAIL_LIMIT)) * (1 - PEN_SIZE_DETAIL_FRACTION);
  return Math.round(normalized * PEN_SIZE_SLIDER_STEPS);
}

function sliderValueToPenSize(value) {
  const normalized = clampNumber(value, 0, PEN_SIZE_SLIDER_STEPS, 0) / PEN_SIZE_SLIDER_STEPS;
  if (normalized <= PEN_SIZE_DETAIL_FRACTION) {
    return Math.round(
      PEN_SIZE_MIN
      + (normalized / PEN_SIZE_DETAIL_FRACTION) * (PEN_SIZE_DETAIL_LIMIT - PEN_SIZE_MIN),
    );
  }
  return Math.round(
    PEN_SIZE_DETAIL_LIMIT
    + ((normalized - PEN_SIZE_DETAIL_FRACTION) / (1 - PEN_SIZE_DETAIL_FRACTION))
      * (PEN_SIZE_MAX - PEN_SIZE_DETAIL_LIMIT),
  );
}

function PenSizeControl({ value, onChange, disabled = false }) {
  const safeValue = Math.round(clampNumber(value, PEN_SIZE_MIN, PEN_SIZE_MAX, PEN_SIZE_MIN));

  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5, opacity: disabled ? 0.5 : 1 }}>
      <span style={{ display: 'flex', justifyContent: 'space-between', gap: 10, color: 'rgba(255,255,255,0.88)', fontSize: 12, fontWeight: 800 }}>
        <span>Size</span>
        <span>{safeValue}px</span>
      </span>
      <input
        type="range"
        min={0}
        max={PEN_SIZE_SLIDER_STEPS}
        step={1}
        value={penSizeToSliderValue(safeValue)}
        disabled={disabled}
        onChange={(event) => {
          if (typeof onChange === 'function') onChange(sliderValueToPenSize(event.target.value));
        }}
        style={{ width: '100%', margin: 0, accentColor: 'rgba(255,255,255,0.92)' }}
      />
    </label>
  );
}


const PENCIL_SIZE_MIN = 1;
const PENCIL_SIZE_DETAIL_LIMIT = 50;
const PENCIL_SIZE_MAX = 1000;
const PENCIL_SIZE_DETAIL_FRACTION = 0.8;
const PENCIL_SIZE_SLIDER_STEPS = 10000;

function pencilSizeToSliderValue(value) {
  const size = clampNumber(value, PENCIL_SIZE_MIN, PENCIL_SIZE_MAX, PENCIL_SIZE_MIN);
  const normalized = size <= PENCIL_SIZE_DETAIL_LIMIT
    ? ((size - PENCIL_SIZE_MIN) / (PENCIL_SIZE_DETAIL_LIMIT - PENCIL_SIZE_MIN))
      * PENCIL_SIZE_DETAIL_FRACTION
    : PENCIL_SIZE_DETAIL_FRACTION
      + ((size - PENCIL_SIZE_DETAIL_LIMIT) / (PENCIL_SIZE_MAX - PENCIL_SIZE_DETAIL_LIMIT))
        * (1 - PENCIL_SIZE_DETAIL_FRACTION);
  return Math.round(normalized * PENCIL_SIZE_SLIDER_STEPS);
}

function sliderValueToPencilSize(value) {
  const normalized = clampNumber(value, 0, PENCIL_SIZE_SLIDER_STEPS, 0)
    / PENCIL_SIZE_SLIDER_STEPS;

  if (normalized <= PENCIL_SIZE_DETAIL_FRACTION) {
    return Math.round(
      PENCIL_SIZE_MIN
      + (normalized / PENCIL_SIZE_DETAIL_FRACTION)
        * (PENCIL_SIZE_DETAIL_LIMIT - PENCIL_SIZE_MIN),
    );
  }

  return Math.round(
    PENCIL_SIZE_DETAIL_LIMIT
    + ((normalized - PENCIL_SIZE_DETAIL_FRACTION) / (1 - PENCIL_SIZE_DETAIL_FRACTION))
      * (PENCIL_SIZE_MAX - PENCIL_SIZE_DETAIL_LIMIT),
  );
}

function PencilSizeControl({ value, onChange, disabled = false }) {
  const safeValue = Math.round(
    clampNumber(value, PENCIL_SIZE_MIN, PENCIL_SIZE_MAX, PENCIL_SIZE_MIN),
  );

  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5, opacity: disabled ? 0.5 : 1 }}>
      <span style={{ display: 'flex', justifyContent: 'space-between', gap: 10, color: 'rgba(255,255,255,0.88)', fontSize: 12, fontWeight: 800 }}>
        <span>Size</span>
        <span>{safeValue}px</span>
      </span>
      <input
        type="range"
        min={0}
        max={PENCIL_SIZE_SLIDER_STEPS}
        step={1}
        value={pencilSizeToSliderValue(safeValue)}
        disabled={disabled}
        onChange={(event) => {
          if (typeof onChange === 'function') {
            onChange(sliderValueToPencilSize(event.target.value));
          }
        }}
        style={{ width: '100%', margin: 0, accentColor: 'rgba(255,255,255,0.92)' }}
      />
    </label>
  );
}

function RangeControl({ label, value, min, max, step = 1, suffix = '', onChange, disabled = false }) {
  const safeValue = clampNumber(value, min, max, min);

  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5, opacity: disabled ? 0.5 : 1 }}>
      <span style={{ display: 'flex', justifyContent: 'space-between', gap: 10, color: 'rgba(255,255,255,0.88)', fontSize: 12, fontWeight: 800 }}>
        <span>{label}</span>
        <span>{Math.round(safeValue)}{suffix}</span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={safeValue}
        disabled={disabled}
        onChange={(event) => {
          if (typeof onChange === 'function') onChange(Number(event.target.value));
        }}
        style={{ width: '100%', margin: 0, accentColor: 'rgba(255,255,255,0.92)' }}
      />
    </label>
  );
}

function ToggleControl({ label, checked, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, color: 'rgba(255,255,255,0.88)', fontSize: 12, fontWeight: 800 }}>
      <span>{label}</span>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(event) => {
          if (typeof onChange === 'function') onChange(event.target.checked);
        }}
      />
    </label>
  );
}

export default function CanvasToolControlsPanel({
  activeTool,
  activeBrushPreset,
  brushSize,
  setBrushSize,
  brushSoftness,
  setBrushSoftness,
  activeToolOpacity,
  onToolOpacityChange,
  jitterAmount,
  setJitterAmount,
  calligraphySettings,
  setCalligraphySettings,
  airbrushDistance,
  setAirbrushDistance,
  airbrushPaintFlow,
  setAirbrushPaintFlow,
  airbrushPressurePaintFlowEnabled,
  setAirbrushPressurePaintFlowEnabled,
  airbrushAirPressure,
  setAirbrushAirPressure,
  transformMode = 'scale',
  setTransformMode,
  perspectiveAxis = 'vertical',
  setPerspectiveAxis,
  onTransformCommand,
  modelViewOpen = false,
  paintMaterialSettings,
  onPaintMaterialSettingsChange,
  pbrBrushActive = false,
  lock3dPaintToStartingSurface = false,
  onLock3dPaintToStartingSurfaceChange,
}) {
  const isTransformTool = activeTool === 'move';
  const isViewTool = isTransformTool;
  const isSelectionTool = activeTool === 'marquee_rect' || activeTool === 'marquee_ellipse' || activeTool === 'marquee_polygon' || activeTool === 'marquee_polygon_edit';
  const isShapeTool = activeTool === 'line' || activeTool === 'rectangle' || activeTool === 'ellipse';
  const isLiquify = activeTool === 'liquify';
  const isPaint = activeTool === 'paint';
  const isBucket = activeTool === 'bucket';
  const isEraser = activeTool === 'eraser';
  const isNormalPen = activeTool === 'pen' && activeBrushPreset === 'pen';
  const isPencil = activeTool === 'pen' && activeBrushPreset === 'pencil';
  const isAirbrush = activeTool === 'pen' && activeBrushPreset === 'airbrush';
  const isCalligraphy = activeTool === 'pen' && activeBrushPreset === 'calligraphy';
  const isJitter = activeTool === 'pen' && activeBrushPreset === 'jitter';
  const hasGenericBrushControls = !isViewTool && !isSelectionTool && !isBucket && !isLiquify && !isPaint;

  // The Paint Tool has its own settings panel in 2D and 3D alike; the 3D
  // projection-brush controls below do not apply to it.
  if (isPaint && modelViewOpen) return null;
  if (!modelViewOpen && !hasGenericBrushControls && !isAirbrush && !isCalligraphy && !isJitter && !isShapeTool && !isTransformTool) return null;

  const calligraphy = calligraphySettings && typeof calligraphySettings === 'object' ? calligraphySettings : {};
  const updateCalligraphy = (key, value) => {
    if (typeof setCalligraphySettings !== 'function') return;
    setCalligraphySettings((current) => ({ ...(current || {}), [key]: value }));
  };

  const materialSettings = paintMaterialSettings && typeof paintMaterialSettings === 'object'
    ? paintMaterialSettings
    : { gloss: 35, height: 12, metalness: 0 };
  const updatePaintMaterialSetting = (key, value) => {
    if (typeof onPaintMaterialSettingsChange !== 'function') return;
    onPaintMaterialSettingsChange(key, value);
  };

  return (
    <section style={{ border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, background: 'rgba(0,0,0,0.16)', padding: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ color: 'rgba(255,255,255,0.96)', fontSize: 13, fontWeight: 900 }}>Tool controls</div>
      {/* How the tool meets the document's paper (2D; 3D tools paint the model instead). */}
      {!modelViewOpen && (isEraser || isNormalPen || isAirbrush || isCalligraphy) ? (
        <CanvasSurfaceMediaControls kind={isEraser ? 'eraser' : isAirbrush ? 'airbrush' : 'pen'} />
      ) : null}

      {isTransformTool ? (
        <>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ color: 'rgba(255,255,255,0.88)', fontSize: 12, fontWeight: 800 }}>Transform mode</span>
            <select
              value={transformMode}
              onChange={(event) => setTransformMode && setTransformMode(event.target.value)}
              style={{ width: '100%', height: 32, borderRadius: 5, border: '1px solid rgba(255,255,255,0.14)', background: '#2b2b2b', color: '#fff', padding: '0 8px', fontWeight: 800 }}
            >
              <option value="scale">Free Transform</option>
              <option value="skew">Skew</option>
              <option value="distort">Distort</option>
              <option value="perspective">Perspective</option>
              <option value="cropLayer">Crop Layer</option>
            </select>
          </label>
          {transformMode === 'perspective' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ color: 'rgba(255,255,255,0.88)', fontSize: 12, fontWeight: 800 }}>Perspective direction</span>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {[
                  ['vertical', 'Vertical'],
                  ['horizontal', 'Horizontal'],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setPerspectiveAxis && setPerspectiveAxis(value)}
                    style={{
                      minHeight: 32,
                      borderRadius: 5,
                      border: perspectiveAxis === value ? '1px solid rgba(120,180,255,0.92)' : '1px solid rgba(255,255,255,0.13)',
                      background: perspectiveAxis === value ? 'rgba(47,128,255,0.34)' : 'rgba(255,255,255,0.065)',
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 850,
                      cursor: 'pointer',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          {transformMode === 'cropLayer' ? (
            <button
              type="button"
              onClick={() => onTransformCommand && onTransformCommand('applyLayerCrop')}
              style={{ minHeight: 34, borderRadius: 5, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(47,128,255,0.28)', color: '#fff', fontSize: 12, fontWeight: 900, cursor: 'pointer' }}
            >
              Apply Layer Crop
            </button>
          ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {[
              ['rotate90ccw', 'Rotate 90° CCW'],
              ['rotate90cw', 'Rotate 90° CW'],
              ['rotate180', 'Rotate 180°'],
              ['flipHorizontal', 'Flip Horizontal'],
              ['flipVertical', 'Flip Vertical'],
            ].map(([command, label]) => (
              <button
                key={command}
                type="button"
                onClick={() => onTransformCommand && onTransformCommand(command)}
                style={{ minHeight: 31, borderRadius: 5, border: '1px solid rgba(255,255,255,0.13)', background: 'rgba(255,255,255,0.065)', color: '#fff', fontSize: 11, fontWeight: 850, cursor: 'pointer' }}
              >
                {label}
              </button>
            ))}
          </div>
          )}
          <div style={{ color: 'rgba(255,255,255,0.62)', fontSize: 11, lineHeight: 1.35 }}>{transformMode === 'cropLayer' ? 'Drag the crop handles, then apply. Pixels outside the crop box are deleted from this layer only.' : 'Drag inside to move. Free Transform handles resizing and rotation; drag just outside a corner to rotate.'}</div>
        </>
      ) : null}

      {!isTransformTool && !isAirbrush && !isCalligraphy ? (
        isEraser ? (
          <EraserSizeControl value={brushSize} onChange={setBrushSize} />
        ) : isNormalPen ? (
          <PenSizeControl value={brushSize} onChange={setBrushSize} />
        ) : isPencil ? (
          <PencilSizeControl value={brushSize} onChange={setBrushSize} />
        ) : (
          <RangeControl label="Size" value={brushSize} min={1} max={2000} suffix="px" onChange={setBrushSize} />
        )
      ) : null}

      {!isTransformTool && !isCalligraphy ? (
        <RangeControl label={isPencil ? 'Darkness' : 'Opacity'} value={Math.round(clampNumber(activeToolOpacity, 0.01, 1, 1) * 100)} min={1} max={100} suffix="%" onChange={(value) => onToolOpacityChange && onToolOpacityChange(value / 100)} />
      ) : null}

      {!isTransformTool && !isAirbrush && !isCalligraphy && !isPencil ? (
        <RangeControl label="Softness" value={brushSoftness} min={0} max={100} suffix="%" onChange={setBrushSoftness} />
      ) : null}

      {isJitter ? (
        <RangeControl label="Jitter" value={jitterAmount} min={0} max={100} suffix="%" onChange={setJitterAmount} />
      ) : null}

      {isAirbrush ? (
        <>
          <RangeControl label="Paint flow" value={airbrushPaintFlow} min={0} max={100} step={20} suffix="%" onChange={setAirbrushPaintFlow} />
          <ToggleControl label="Pen pressure" checked={airbrushPressurePaintFlowEnabled !== false} onChange={setAirbrushPressurePaintFlowEnabled} />
          <RangeControl label="Air pressure" value={airbrushAirPressure} min={5} max={35} step={5} suffix=" PSI" onChange={setAirbrushAirPressure} />
          <RangeControl label="Distance" value={airbrushDistance} min={MIN_AIRBRUSH_DISTANCE} max={MAX_AIRBRUSH_DISTANCE} suffix="mm" onChange={setAirbrushDistance} />
        </>
      ) : null}

      {isCalligraphy ? (
        <>
          <RangeControl label="Thinness" value={clampNumber(calligraphy.fixation, 0, 1, 0.5) * 100} min={0} max={100} suffix="%" onChange={(value) => updateCalligraphy('fixation', value / 100)} />
          <RangeControl label="Width" value={calligraphy.width ?? brushSize} min={1} max={2000} suffix="px" onChange={(value) => updateCalligraphy('width', Math.round(value))} />
          <RangeControl label="Angle" value={calligraphy.angle ?? 0} min={-180} max={180} suffix="°" onChange={(value) => updateCalligraphy('angle', value)} />
          <RangeControl label="Opacity" value={Math.round(clampNumber(activeToolOpacity, 0.01, 1, 1) * 100)} min={1} max={100} suffix="%" onChange={(value) => onToolOpacityChange && onToolOpacityChange(value / 100)} />
          <RangeControl label="Softness" value={brushSoftness} min={0} max={100} suffix="%" onChange={setBrushSoftness} />
          <RangeControl label="Deposit" value={clampNumber(calligraphy.angleDeposit, 0, 1, 0.35) * 100} min={0} max={100} suffix="%" onChange={(value) => updateCalligraphy('angleDeposit', value / 100)} />
        </>
      ) : null}

      {modelViewOpen ? (
        <>
          <div style={{ height: 1, background: 'rgba(255,255,255,0.10)', margin: '2px 0' }} />
          <div style={{ color: 'rgba(255,255,255,0.96)', fontSize: 13, fontWeight: 900 }}>3D paint behaviour</div>
          <ToggleControl
            label="Lock to centre-hit surface"
            checked={lock3dPaintToStartingSurface}
            onChange={onLock3dPaintToStartingSurfaceChange}
          />
          <div style={{ color: 'rgba(255,255,255,0.62)', fontSize: 11, lineHeight: 1.35 }}>
            Uses only the exact centre pixel of the first dab to choose the model surface for the whole stroke.
          </div>
          <div style={{ height: 1, background: 'rgba(255,255,255,0.10)', margin: '2px 0' }} />
          <div style={{ color: 'rgba(255,255,255,0.96)', fontSize: 13, fontWeight: 900 }}>3D paint material</div>
          <RangeControl label="Gloss" value={materialSettings.gloss ?? 35} min={0} max={100} suffix="" disabled={pbrBrushActive} onChange={(value) => updatePaintMaterialSetting('gloss', value)} />
          <RangeControl label="Height / bump" value={materialSettings.height ?? 12} min={0} max={100} suffix="" onChange={(value) => updatePaintMaterialSetting('height', value)} />
          <RangeControl label="Metallic" value={materialSettings.metalness ?? 0} min={0} max={100} suffix="" disabled={pbrBrushActive} onChange={(value) => updatePaintMaterialSetting('metalness', value)} />
          <div style={{ color: 'rgba(255,255,255,0.62)', fontSize: 11, lineHeight: 1.35 }}>
            {pbrBrushActive
              ? 'PBR supplies gloss and metallic from its own maps. Height / bump scales the PBR surface detail.'
              : 'These values are captured when a new 3D stroke begins. Existing strokes keep their own material settings.'}
          </div>
        </>
      ) : null}
    </section>
  );
}
